import React from 'react';
import { Node } from '@xyflow/react';
import { X, Plus, Trash2 } from 'lucide-react';
import {
  WorkflowNodeData,
  TriggerNodeData,
  BranchNodeData,
  SwitchNodeData,
  MergeNodeData,
  StopNodeData,
  ActionNodeData,
  DelayNodeData,
  LoopNodeData,
  BRANCH_FIELD_OPTIONS,
} from '../types';
import { TriggerConfig } from './TriggerConfig';
import { BranchConfig } from './BranchConfig';
import { ActionConfig } from './ActionConfig';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { FieldValueSelect } from './FieldValueSelect';

interface Props {
  node: Node<WorkflowNodeData> | null;
  onClose: () => void;
  onUpdate: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
}

const KIND_LABELS: Record<string, string> = {
  trigger:  'Configure Trigger',
  classify: 'AI Classification',
  branch:   'Configure Condition',
  switch:   'Configure Switch',
  merge:    'Merge Paths',
  action:   'Configure Action',
  delay:    'Configure Delay',
  stop:     'Configure Stop',
  loop:     'Configure Loop',
  end:      'End',
};

export function NodeConfigPanel({ node, onClose, onUpdate }: Props) {
  if (!node) return null;

  const data = node.data as WorkflowNodeData;
  const title = KIND_LABELS[data.kind] ?? 'Configure';

  const handleChange = (updated: Partial<WorkflowNodeData>) => {
    onUpdate(node.id, updated);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        <button
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:text-gray-500"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {data.kind === 'trigger' && (
          <TriggerConfig
            data={data as TriggerNodeData}
            onChange={handleChange}
          />
        )}

        {data.kind === 'classify' && (
          <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
            <p className="font-medium">🤖 Automatic AI Classification</p>
            <p className="mt-2 text-xs">
              This step automatically detects the ticket type from the subject and description,
              matching it to your organisation's defined ticket types.
            </p>
            <p className="mt-2 text-xs text-blue-600 dark:text-blue-300">
              No configuration needed — it adapts as you add or remove ticket types.
            </p>
          </div>
        )}

        {data.kind === 'branch' && (
          <BranchConfig
            data={data as BranchNodeData}
            onChange={handleChange}
          />
        )}

        {data.kind === 'action' && (
          <ActionConfig
            data={data as ActionNodeData}
            onChange={handleChange}
          />
        )}

        {data.kind === 'delay' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">Wait duration</label>
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                className="w-24 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                value={Math.round((data as DelayNodeData).delaySeconds / 60)}
                onChange={(e) =>
                  handleChange({ delaySeconds: parseInt(e.target.value, 10) * 60 } as any)
                }
              />
              <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">minutes</span>
            </div>
          </div>
        )}

        {data.kind === 'switch' && (
          <SwitchConfig
            data={data as SwitchNodeData}
            onChange={handleChange}
          />
        )}

        {data.kind === 'merge' && (
          <div className="rounded-lg bg-teal-50 p-4 text-sm text-teal-800 dark:bg-teal-900/20 dark:text-teal-200">
            <p className="font-medium">Merge Paths</p>
            <p className="mt-2 text-xs">
              This node converges multiple branches into a single path.
              Connect multiple branches to the inputs and the workflow will continue
              from the single output.
            </p>
          </div>
        )}

        {data.kind === 'stop' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
              Stop Reason (optional)
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              rows={3}
              placeholder="e.g., Invalid ticket type, Missing required data..."
              value={(data as StopNodeData).reason || ''}
              onChange={(e) => handleChange({ reason: e.target.value } as any)}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              This node stops the workflow execution. Use it to terminate a branch
              early when certain conditions are not met.
            </p>
          </div>
        )}

        {data.kind === 'loop' && (
          <LoopConfig
            data={data as LoopNodeData}
            onChange={handleChange}
          />
        )}

        {data.kind === 'end' && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            The workflow ends here. No further actions will run.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Loop Node Configuration ───────────────────────────────────────────────────
interface LoopConfigProps {
  data: LoopNodeData;
  onChange: (updated: Partial<LoopNodeData>) => void;
}

function LoopConfig({ data, onChange }: LoopConfigProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-cyan-50 p-3 text-xs text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-200">
        Iterates over an array and runs the block on the <strong>Each item</strong> handle once per
        item. Connect <strong>Each item</strong> to a single Action, and <strong>After</strong> to
        continue once all iterations finish. Tip: add a "Fetch line items" action before the loop.
      </div>

      {/* Array to iterate */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
          Array to iterate (context path)
        </label>
        <input
          type="text"
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          placeholder="entity.lineItems"
          value={data.itemsPath || ''}
          onChange={(e) => onChange({ itemsPath: e.target.value })}
        />
      </div>

      {/* Item variable */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
          Item variable name
        </label>
        <input
          type="text"
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          placeholder="lineItem"
          value={data.itemVariable || ''}
          onChange={(e) => onChange({ itemVariable: e.target.value })}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          The current item is available to the body as this variable.
        </p>
      </div>

      {/* Execution mode */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
          Execution mode
        </label>
        <ThemedSelect
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={data.mode || 'serial'}
          onChange={(e) => onChange({ mode: e.target.value as LoopNodeData['mode'] })}
        >
          <option value="serial">Serial (one at a time)</option>
          <option value="parallel">Parallel (all at once)</option>
          <option value="parallel_limited">Parallel (limited concurrency)</option>
        </ThemedSelect>
      </div>

      {/* Concurrency (only for parallel_limited) */}
      {data.mode === 'parallel_limited' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
            Max concurrency
          </label>
          <input
            type="number"
            min={1}
            className="w-24 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            value={data.concurrency ?? 5}
            onChange={(e) => onChange({ concurrency: Math.max(1, parseInt(e.target.value, 10) || 1) })}
          />
        </div>
      )}

      {/* Max iterations */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
          Max iterations (safety cap)
        </label>
        <input
          type="number"
          min={1}
          className="w-28 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          placeholder="1000"
          value={data.maxIterations ?? ''}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            onChange({ maxIterations: Number.isFinite(v) && v > 0 ? v : undefined });
          }}
        />
      </div>

      {/* Result variable */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
          Result variable
        </label>
        <input
          type="text"
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          placeholder="loopResult"
          value={data.outputVariable || ''}
          onChange={(e) => onChange({ outputVariable: e.target.value })}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          The aggregate result is written to <code>context.metadata.{data.outputVariable || 'loopResult'}</code>.
        </p>
      </div>

      {/* Continue on error */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="continueOnError"
          checked={data.continueOnError ?? true}
          onChange={(e) => onChange({ continueOnError: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
        />
        <label htmlFor="continueOnError" className="text-sm text-gray-700 dark:text-gray-200">
          Continue after a failed iteration
        </label>
      </div>
    </div>
  );
}

// ── Switch Node Configuration ─────────────────────────────────────────────────
interface SwitchConfigProps {
  data: SwitchNodeData;
  onChange: (updated: Partial<SwitchNodeData>) => void;
}

function SwitchConfig({ data, onChange }: SwitchConfigProps) {
  const addCase = () => {
    onChange({
      cases: [...(data.cases || []), { value: '', label: '' }],
    });
  };

  const removeCase = (index: number) => {
    const newCases = [...(data.cases || [])];
    newCases.splice(index, 1);
    onChange({ cases: newCases });
  };

  const updateCase = (index: number, value: string, label?: string) => {
    const newCases = [...(data.cases || [])];
    newCases[index] = { value, label: label || value };
    onChange({ cases: newCases });
  };

  return (
    <div className="space-y-4">
      {/* Field selector */}
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-200">
          Switch on field
        </label>
        <ThemedSelect
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={data.field || ''}
          // A new field means the old case values no longer apply.
          onChange={(e) => onChange({ field: e.target.value, cases: (data.cases || []).map(() => ({ value: '', label: '' })) })}
        >
          <option value="">Select field...</option>
          {BRANCH_FIELD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ThemedSelect>
      </div>

      {/* Cases */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-200">Cases</label>
          <button
            type="button"
            onClick={addCase}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20"
          >
            <Plus size={12} />
            Add Case
          </button>
        </div>

        <div className="space-y-2">
          {(data.cases || []).map((c, index) => (
            <div key={index} className="flex items-center gap-2">
              {/* Real values (stages, statuses, …) instead of typing an id; the
                  case label keeps the readable name shown on the canvas. */}
              <div className="flex-1">
                <FieldValueSelect
                  valueType={BRANCH_FIELD_OPTIONS.find((f) => f.value === data.field)?.valueType ?? 'text'}
                  value={String(c.value)}
                  ariaLabel={`Case ${index + 1}`}
                  onChange={(value, label) => updateCase(index, value, label)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeCase(index)}
                className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          {(data.cases || []).length === 0 && (
            <p className="py-2 text-xs text-gray-400 dark:text-gray-500 text-center">
              No cases defined. Add a case to create an output handle.
            </p>
          )}
        </div>
      </div>

      {/* Default case toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="hasDefault"
          checked={data.hasDefault ?? true}
          onChange={(e) => onChange({ hasDefault: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
        <label htmlFor="hasDefault" className="text-sm text-gray-700 dark:text-gray-200">
          Include default case
        </label>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Each case creates a separate output handle. The default case catches all
        unmatched values.
      </p>
    </div>
  );
}
